import { DEFAULT_SALES_CALL_SCRIPT, DEFAULT_SALES_CALL_SCRIPT_SLUG } from "@swivl-mono/types";
import type { CallScriptInterface, CallScriptSource, ResolvedCallScript } from "@swivl-mono/types";

export type CallScriptRowLike = Pick<CallScriptInterface, "id" | "groupId" | "locationId" | "slug" | "name" | "whenToUse" | "steps"> &
  Partial<Pick<CallScriptInterface, "forSequencesOnly">>;

export interface CallScriptScope {
  /** The location's group when a location is known; the number's / widget's group otherwise. */
  groupId?: number | null;
  locationId?: number | null;
}

/** How the conversation started, for scripts bound to sequence days (ENG-3403). */
export interface CallScriptConversationOrigin {
  /** The slug the originating sequence day names; rendered first. */
  startingScriptSlug?: string | null;
}

function rowSource(row: CallScriptRowLike): CallScriptSource {
  if (row.locationId != null) return "location";
  if (row.groupId != null) return "group";
  return "org";
}

const RANK: Record<CallScriptSource, number> = { platform: 0, org: 1, group: 2, location: 3 };

/**
 * The scripts in force at a scope (ENG-3356). Union of slugs across org →
 * group → location; the nearest row wins per slug; a nearest row with no steps
 * removes that script here; the platform Sales script applies wherever no row
 * claims its slug. Pure — the caller loads the org's rows.
 */
export function resolveCallScripts(
  rows: CallScriptRowLike[],
  scope: CallScriptScope,
  origin: CallScriptConversationOrigin = {}
): ResolvedCallScript[] {
  const winners = new Map<string, { row: CallScriptRowLike; source: CallScriptSource }>();
  for (const row of rows) {
    const source = rowSource(row);
    if (source === "group" && row.groupId !== (scope.groupId ?? null)) continue;
    if (source === "location" && row.locationId !== (scope.locationId ?? null)) continue;
    const current = winners.get(row.slug);
    if (!current || RANK[source] > RANK[current.source]) winners.set(row.slug, { row, source });
  }

  const startingSlug = origin.startingScriptSlug ?? null;
  const out: ResolvedCallScript[] = [];
  for (const [slug, { row, source }] of winners) {
    if (!row.steps || row.steps.length === 0) continue; // removed at this scope
    out.push({
      slug,
      name: row.name,
      whenToUse: row.whenToUse,
      steps: row.steps,
      forSequencesOnly: !!row.forSequencesOnly,
      source,
      rowId: row.id,
      ...(slug === startingSlug ? { startingScript: true } : {})
    });
  }
  if (!winners.has(DEFAULT_SALES_CALL_SCRIPT_SLUG)) {
    out.push({
      ...DEFAULT_SALES_CALL_SCRIPT,
      source: "platform",
      rowId: null,
      ...(DEFAULT_SALES_CALL_SCRIPT_SLUG === startingSlug ? { startingScript: true } : {})
    });
  }
  // The starting script first (the sequence day named it), then Sales, then by name — a
  // stable order for the prompt and the page.
  out.sort((a, b) => {
    if (a.startingScript !== b.startingScript) return a.startingScript ? -1 : 1;
    if (a.slug === DEFAULT_SALES_CALL_SCRIPT_SLUG) return -1;
    if (b.slug === DEFAULT_SALES_CALL_SCRIPT_SLUG) return 1;
    return a.name.localeCompare(b.name);
  });
  return out;
}

/**
 * The scripts the bot may see in THIS conversation (ENG-3403): everything in force, minus scripts
 * marked "for sequences only" unless the conversation's sequence day named that script.
 */
export function callScriptsForConversation(resolved: ResolvedCallScript[]): ResolvedCallScript[] {
  return resolved.filter((script) => !script.forSequencesOnly || script.startingScript);
}
